import { useSyncExternalStore } from "react";
import { addDays, todayISO } from "@/engine/date";

/** 휴지통에 머무는 날. 화면의 예정일을 계산할 때만 쓴다. */
export const WORKSPACE_TRASH_DAYS = 30;

export type WorkspaceTrashItem = {
  id: string;
  name: string;
  by: string;
  deleted: string;
  purge: string;
};

export type WorkspaceDangerState = {
  archived: boolean;
  transferredTo: string | null;
  departed: boolean;
  trash: WorkspaceTrashItem[];
};

function freshTrash(today = todayISO()): WorkspaceTrashItem[] {
  const deleted = addDays(today, -27);
  return [{ id: "ws-old", name: "옛 견적", by: "나", deleted, purge: addDays(deleted, WORKSPACE_TRASH_DAYS) }];
}

let state: WorkspaceDangerState = { archived: false, transferredTo: null, departed: false, trash: freshTrash() };
const listeners = new Set<() => void>();

function emit() {
  state = {
    ...state,
    trash: state.trash.map((item) => ({ ...item })),
  };
  for (const listener of listeners) listener();
}

export function resetWorkspaceDanger(): void {
  state = { archived: false, transferredTo: null, departed: false, trash: freshTrash() };
  emit();
}

export function getWorkspaceDanger(): WorkspaceDangerState {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useWorkspaceDanger(): WorkspaceDangerState {
  return useSyncExternalStore(subscribe, getWorkspaceDanger, getWorkspaceDanger);
}

/** 위험 영역 탭은 소유자와 관리자만 있다. */
export function workspaceDangerTab(role: string): boolean {
  return role === "owner" || role === "admin";
}

/** 소유자는 이전한 뒤에만 나간다. 관리자는 바로 나갈 수 있다. */
export function canLeaveWorkspace(role: string, transferred: boolean): boolean {
  if (role === "admin" || role === "editor" || role === "viewer") return true;
  return role === "owner" && transferred;
}

export function archiveWorkspace(): void {
  state = { ...state, archived: true };
  emit();
}

export function restoreWorkspaceArchive(): void {
  state = { ...state, archived: false };
  emit();
}

export function transferWorkspace(adminName: string): boolean {
  const name = adminName.trim();
  if (!name || state.transferredTo) return false;
  state = { ...state, transferredTo: name };
  emit();
  return true;
}

export function leaveWorkspace(role: string): boolean {
  if (!canLeaveWorkspace(role, state.transferredTo != null) || state.departed) return false;
  state = { ...state, departed: true };
  emit();
  return true;
}

/** 목록에서 빼지 않고 휴지통 행만 더한다. 같은 이름은 한 번이다. */
export function trashWorkspace(name: string, today = todayISO()): boolean {
  const trimmed = name.trim();
  if (!trimmed || state.trash.some((item) => item.name === trimmed)) return false;
  state = {
    ...state,
    trash: [
      ...state.trash,
      {
        id: `ws-${state.trash.length + 1}`,
        name: trimmed,
        by: "나",
        deleted: today,
        purge: addDays(today, WORKSPACE_TRASH_DAYS),
      },
    ],
  };
  emit();
  return true;
}

export function restoreWorkspaceTrash(id: string): void {
  state = { ...state, trash: state.trash.filter((item) => item.id !== id) };
  emit();
}

export function transferImpact(): string {
  return "이전하면 나는 관리자가 됩니다.";
}

export function leaveWorkspaceImpact(): string {
  return "다시 들어오려면 초대를 받아야 합니다.";
}

export function trashWorkspaceImpact(): string {
  return "30일 동안 휴지통에서 복구할 수 있습니다.";
}
